import { z } from 'zod'

import { moneySchema } from '@/schemas/common'
import { tableQuerySchema } from '@/schemas/table'

/**
 * Market · Movers — one table, five orderings, and a sixth tab for brokers.
 *
 * The five table tabs are not five endpoints: Gainers and Losers are the same
 * session sorted in opposite directions, and Turnover, Volume and
 * Transactions re-sort the identical rows. Modelling them as a `view` on one
 * query keeps that true, and keeps the sector filter meaningful across a tab
 * switch instead of resetting five times. The Brokers tab is a different feed
 * (`/market/brokers/`), so it is not one of `MOVERS_VIEWS`; the screen's own
 * search schema adds it.
 */

export const MOVERS_VIEWS = ['gainers', 'losers', 'turnover', 'volume', 'transactions'] as const

export const moversViewSchema = z.enum(MOVERS_VIEWS)

/**
 * One row of the movers table.
 *
 * The frame draws seven columns and stops at Change, but Turnover and
 * Transactions are two of the five tabs — so the row carries the figures those
 * tabs sort on even though no column prints them today. Deriving turnover from
 * `ltp × volumeKitta` would be a float multiplication over money, which is the
 * one thing the `Money` brand exists to prevent.
 */
export const moverRowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  /** The "Price" column: the last traded price. */
  ltp: moneySchema,
  high: moneySchema,
  open: moneySchema,
  low: moneySchema,
  volumeKitta: z.number().int(),
  /** Rupees, unscaled — the component picks Cr or Arba. `null` when mm-be
   *  reports no turnover for this name. */
  turnoverRs: moneySchema.nullable(),
  transactions: z.number().int(),
  percentChange: moneySchema,
})

export const moversQuerySchema = tableQuerySchema.extend({
  view: moversViewSchema,
  /** Kebab sector id, as `sectorSlug` builds it. Absent means all sectors. */
  sector: z.string().min(1).optional(),
})

/**
 * One row of the Brokers tab: a broker's kitta bought and sold today and the net of the two.
 * From `/market/brokers/`, which ranks the session's brokers by turnover and answers at most
 * fifty of them.
 */
export const moverBrokerRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  boughtKitta: z.number().int(),
  soldKitta: z.number().int(),
  netKitta: z.number().int(),
})

export type MoversView = z.infer<typeof moversViewSchema>
export type MoverRow = z.infer<typeof moverRowSchema>
export type MoversQuery = z.infer<typeof moversQuerySchema>
export type MoverBrokerRow = z.infer<typeof moverBrokerRowSchema>
